import {Box, BoxProps, Flex, FlexProps, StackProps, TextProps, useMultiStyleConfig, VStack, Text, Link, LinkProps} from '@chakra-ui/react'
import {CardPhotoVideoObject} from '_types'
import {Picture, Video, CtaButton, LiveText} from '_uiComponents'
import React, {useEffect, useRef, useState} from 'react'
import {createRedirectUrl, updateCloudinaryPDImage, updateCloudinaryPDPoster, buildVideoSources, buildEditorialVideoAnalyticsTitle} from '_utils'
import type {VideoSlotName} from '_utils'
import {useIntl} from 'react-intl'
import {useAnalytics, useIsElementInViewport, usePageDesigner, useOpenDrawer} from '_customHooks'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {getProductAnalyticsInfo, getProductImpressionAnalytics} from '_analytics/utils/analyticsUtils'

interface CardPhotoVideoProps {
    propsObject?: CardPhotoVideoObject
    onClick?: () => void
    classes?: string
    isVisible?: boolean
    idProductCta?: string
    analyticsList?: string
    position?: number
    isDoubleSame?: boolean
    adaptiveHeight?: boolean
    isSplitSlide?: boolean
    /** Named video slot for responsive delivery (set by the parent module). */
    videoSlot?: VideoSlotName
    /** Above-the-fold priority: skips lazy-load, eager preload. Driven by the module position. */
    isPriority?: boolean
}

/**
 * @name CardPhotoVideo
 *
 * @component
 *
 * @description
 * Renders a styled Element containing a video or a image with a text and a link.
 *
 * @param {CardPhotoVideoObject} props.propsObject - An object with all the possibile attributes for the component
 * @param {() => void} props.onClick - A function to be called on click of the component or the cta
 * @param {string} props.classes - An string with the classes for the analytics
 * @param {boolean} props.isVisible - A boolean indicating if the component is in the viewport
 * @param {string} props.idProductCta - Position of element in its container to retreive correct prodcut from classes/link\
 * @param {string} props.analyticsList = Category ID from PLP.
 * @param {number} props.position - The position of the element with respect to other page designer elements in the page
 *
 * @returns {JSX.Element} Rendered CardPhotoVideo component.
 */
const CardPhotoVideo: React.FC<CardPhotoVideoProps> = ({
    propsObject,
    onClick,
    classes,
    isVisible = false,
    idProductCta = '',
    analyticsList,
    position,
    isDoubleSame = false,
    adaptiveHeight,
    isSplitSlide = false,
    videoSlot,
    isPriority = false,
}): JSX.Element => {
    const {linkObject, globalImageTransformation, globalVideoTransformation, CloudinaryPageDesignerCNAME} = usePageDesigner()
    const {analyticsProductClick, analyticsProductImpression} = useAnalytics()
    const {formatMessage} = useIntl()
    const [alreadySentProductImpression, setAlreadySentProductImpression] = useState(false)
    const [productID, setProductID] = useState<string | undefined>()
    const containerRef = useRef<HTMLElement>(null)
    const isSelfVisible = useIsElementInViewport(containerRef)

    const defaultValues: CardPhotoVideoObject = {
        type: 'Image',
        imgTitle: '',
        isHoverZoomLeft: false,
        isHoverZoomRight: false,
        padding: false,
        layout: 'full',
        ctaBlank: false,
        ctaStyle: 'text',
        verticalAlign: 'center',
        horizontalAlign: 'center',
        textAlign: 'center',
        animationTime: '1000',
        isLeft: false,
        listingLayout: '',
    }

    const {
        type,
        imgDesk,
        imgTablet,
        imgMobile,
        imgTitle,
        imgDeskRatio,
        imgTabletRatio,
        imgMobileRatio,
        video,
        padding,
        ctaText,
        ctaLink,
        ctaBlank,
        ctaStyle,
        verticalAlign,
        horizontalAlign,
        textAlign,
        title,
        text,
        color,
        isImgZoom,
        isHoverZoomLeft,
        isHoverZoomRight,
        animationTime,
        isAnimationTitle,
        isAnimationCta,
        layout,
        isLeft,
        listingLayout,
        accessibilityImageLivetext,
    } = {...defaultValues, ...Object.fromEntries(Object.entries(propsObject ?? {}).filter(([, v]) => v !== undefined))}
    const styles = useMultiStyleConfig('CardPhotoVideo', {
        padding,
        color: color?.trim(),
        textAlign,
        verticalAlign,
        horizontalAlign,
        isImgZoom: isImgZoom && isSelfVisible,
        isHoverZoomLeft,
        isHoverZoomRight,
        animationTime,
        isAnimationTitle: isAnimationTitle && isSelfVisible,
        variant: layout,
        isLeft,
        isAnimationCta: isAnimationCta && isSelfVisible,
        imgDeskRatio,
        imgTabletRatio,
        imgMobileRatio,
        type,
        isDoubleSame,
        adaptiveHeight,
        isSplitSlide,
    }) as Record<string, StackProps & BoxProps & TextProps & FlexProps & LinkProps>

    const {data: product} = useProduct(
        {
            parameters: {
                id: productID!,
            },
        },
        {
            enabled: !!productID,
        },
    )

    const videoConf = video && JSON.parse(video.playerConf)
    const isModuleClickable = !!ctaLink && !videoConf?.playerConfig?.controls
    const {handleShopTheImageClick} = useOpenDrawer()

    const triggerAnalyticsEventProductClick = (): void => {
        const {productCurrency} = getProductAnalyticsInfo(product!)
        const analyticsProductObject = getProductImpressionAnalytics({
            product: product!,
            analyticsList: analyticsList,
            idx: position ? position - 1 : 0,
        })

        analyticsProductClick({
            currencyCode: productCurrency,
            dimension25: listingLayout,
            actionField: {
                list: analyticsList ? analyticsList : '',
            },
            product: analyticsProductObject,
        })
    }

    useEffect(() => {
        if (isVisible && !alreadySentProductImpression && product) {
            const {productCurrency} = getProductAnalyticsInfo(product)
            const analyticsProductObject = getProductImpressionAnalytics({product, analyticsList: analyticsList, idx: position ? position - 1 : 0})

            analyticsProductImpression({
                currencyCode: productCurrency,
                template: listingLayout,
                impressions: analyticsProductObject,
            })

            setAlreadySentProductImpression(true)
        }
    }, [isVisible, product])

    useEffect(() => {
        let productID: string | undefined = undefined
        if (classes && classes.indexOf(`js-PD-pid${idProductCta}-`) > -1) {
            const classList = classes.split(`js-PD-pid${idProductCta}-`)
            if (classList && classList.length > 0) {
                productID = classList[0].trim()
            }
        }
        if (!productID && ctaLink) {
            const matchProduct = ctaLink.match(/[0-9]{3}[a-zA-Z0-9]+/)
            if (matchProduct) productID = matchProduct[0]
        }
        setProductID(productID)
    }, [])

    const handleLinkClick = (e: React.MouseEvent) => {
        if (handleShopTheImageClick(e, ctaLink)) return
        if (productID && product) triggerAnalyticsEventProductClick()
        if (onClick) onClick()
    }

    const renderBodyContent = (insideLink = false) =>
        title || text || (ctaLink && ctaText) ? (
            <Flex {...styles.bodyWrapper} data-testid="card-photo-video-body" className="cc-content-text">
                <VStack sx={styles.body} className="cc-content-elements">
                    {title && (
                        <Text as="h2" {...styles.title} className="cc-title">
                            {title}
                        </Text>
                    )}
                    {text && (
                        <Text as="p" {...styles.text} className="cc-description">
                            {text}
                        </Text>
                    )}
                    {ctaText && ctaLink && !insideLink && (
                        <Box className={`cardPhotoVideo__btn--${ctaStyle} cc-content-cta${ctaStyle === 'text' ? '' : ' cc-content-button'}`}>
                            <CtaButton
                                variant={ctaStyle}
                                href={createRedirectUrl(ctaLink, linkObject.hostlink, linkObject.locale, linkObject.site)}
                                ctaColor="color.text.white"
                                background="color.text.black"
                                isAnimated={isAnimationCta}
                                animationTime={animationTime}
                                onClick={(e) => {
                                    if (handleShopTheImageClick(e, ctaLink)) return
                                    if (productID && product) triggerAnalyticsEventProductClick()
                                    if (onClick) onClick()
                                }}
                                openInNewTab={!!ctaBlank}
                                data-testid="card-photo-video-btn"
                            >
                                {ctaText}
                            </CtaButton>
                        </Box>
                    )}
                    {ctaText && ctaLink && insideLink && (
                        <Box className={`cardPhotoVideo__btn--${ctaStyle} cc-content-cta${ctaStyle === 'text' ? '' : ' cc-content-button'}`}>
                            <Text as="span" className="cc-content-button cc-content-button-text" data-testid="card-photo-video-btn">
                                {ctaText}
                            </Text>
                        </Box>
                    )}
                </VStack>
            </Flex>
        ) : null

    const isOverlayLayout = layout === 'full' || layout === 'fullOneThird'

    const renderMediaContent = () =>
        type === 'Image' ? (
            imgDesk && (
                <Box sx={styles.image} className="cc-content-image">
                    <Picture
                        overflow="hidden"
                        objectFit="cover"
                        imagesm={updateCloudinaryPDImage(imgMobile || imgTablet || imgDesk, globalImageTransformation, CloudinaryPageDesignerCNAME)}
                        imagemd={updateCloudinaryPDImage(imgTablet || imgDesk, globalImageTransformation, CloudinaryPageDesignerCNAME)}
                        imagelg={updateCloudinaryPDImage(imgDesk, globalImageTransformation, CloudinaryPageDesignerCNAME)}
                        imagexl={updateCloudinaryPDImage(imgDesk, globalImageTransformation, CloudinaryPageDesignerCNAME)}
                        alt={imgTitle}
                        height="100%"
                        position={layout === 'fullOneThird' ? 'relative' : 'absolute'}
                        zIndex={0}
                    />
                </Box>
            )
        ) : (
            <Box sx={styles.video} className="cc-content-video cc-content-video-01">
                <Video
                    overflow="hidden"
                    objectFit="cover"
                    height="100%"
                    isVideoModule={true}
                    zIndex={0}
                    position={isDoubleSame ? 'absolute' : 'static'}
                    autoplay={videoConf && videoConf.playerConfig?.autoplay}
                    controls={videoConf && videoConf.playerConfig?.controls}
                    muted={videoConf && videoConf.playerConfig.muted}
                    loop={videoConf && videoConf.playerConfig.loop}
                    sources={video && videoSlot ? buildVideoSources(video, {slot: videoSlot, transformation: globalVideoTransformation, cname: CloudinaryPageDesignerCNAME}) : undefined}
                    isPriority={isPriority}
                    analyticsTitle={buildEditorialVideoAnalyticsTitle(formatMessage, (position ?? 0) + 1, 1)}
                    poster={video ? updateCloudinaryPDPoster(video, globalVideoTransformation, CloudinaryPageDesignerCNAME) : undefined}
                ></Video>
            </Box>
        )

    return (
        <VStack {...styles.container} data-testid="card-photo-video" className="cc-element" ref={containerRef}>
            {accessibilityImageLivetext && <LiveText>{accessibilityImageLivetext}</LiveText>}
            {isModuleClickable ? (
                isOverlayLayout ? (
                    <>
                        {renderMediaContent()}
                        <Link
                            data-testid="card-photo-video-full-link"
                            {...styles.link}
                            className="cc-link-full"
                            href={createRedirectUrl(ctaLink, linkObject.hostlink, linkObject.locale, linkObject.site)}
                            isExternal={!!ctaBlank}
                            onClick={handleLinkClick}
                        >
                            {renderBodyContent(true)}
                        </Link>
                    </>
                ) : (
                    <Link
                        data-testid="card-photo-video-full-link"
                        {...styles.linkWrapper}
                        className="cc-link-full"
                        href={createRedirectUrl(ctaLink, linkObject.hostlink, linkObject.locale, linkObject.site)}
                        isExternal={!!ctaBlank}
                        onClick={handleLinkClick}
                    >
                        {renderMediaContent()}
                        {renderBodyContent(true)}
                    </Link>
                )
            ) : (
                <>
                    {renderMediaContent()}
                    {renderBodyContent(false)}
                </>
            )}
        </VStack>
    )
}

export {CardPhotoVideo}
